<script setup>
// The marketing "get started" CTA, aware of who is looking at it:
//  * signed out            → /register
//  * admin of no clubs     → straight to club creation
//  * admin of 1+ clubs     → a dropdown: each club you admin (linking to its
//                            admin area — `adminPath` scopes it to a format's
//                            list page, e.g. 'americanos') + "Create a new club".
// Drop-in for the old `<v-btn :to="getStartedTo">` usages: every v-btn prop
// (color, size, rounded, class...) passes straight through.
import {computed, ref} from 'vue';

defineOptions({inheritAttrs: false});

const props = defineProps({
  // Admin-area subpath the club options link to, e.g. 'americanos' or
  // 'leagues'. Empty = the club's admin dashboard.
  adminPath: {type: String, default: ''},
});

const {isAuthenticated, user} = useAuthService();

const adminClubIds = computed(() =>
    (isAuthenticated.value && user.value?.adminFor) ? user.value.adminFor : []);
const hasClubs = computed(() => adminClubIds.value.length > 0);

// One shared fetch of the admin clubs' names for every CTA on the page.
const adminClubs = useState('marketing-cta-admin-clubs', () => null);
const {getItems: getClubs, items: fetchedClubs} = useCrudApi('api/v2/clubs');
const loading = ref(false);
const onMenuOpened = async (open) => {
  if (!open || adminClubs.value !== null || loading.value) return;
  loading.value = true;
  try {
    await getClubs(1, 100, {ids: adminClubIds.value});
    adminClubs.value = (fetchedClubs.value || []).map(club => ({id: club.id, name: club.name}));
  } finally {
    loading.value = false;
  }
};

const clubTo = (clubId) =>
    props.adminPath ? `/admin/clubs/${clubId}/${props.adminPath}` : `/admin/clubs/${clubId}`;
</script>

<template>
  <!-- Signed out: register. Signed in without a club: straight to creation. -->
  <v-btn v-if="!isAuthenticated" v-bind="$attrs" to="/register">
    <slot/>
  </v-btn>
  <v-btn v-else-if="!hasClubs" v-bind="$attrs" :to="{name: 'ClubCreate'}">
    <slot/>
  </v-btn>

  <!-- Club admin: pick which club to set it up at, or start another club. -->
  <v-menu v-else location="bottom start" @update:model-value="onMenuOpened">
    <template #activator="{ props: menu }">
      <v-btn v-bind="{...$attrs, ...menu}" append-icon="mdi-chevron-down">
        <slot/>
      </v-btn>
    </template>
    <!-- Same list anatomy as the app's other dropdowns (LeagueCycleView's
         Print menu): compact items, bold 14px titles, small leading icons. -->
    <v-list class="py-0 mcta-list">
      <div class="mcta-label">Set up at…</div>
      <template v-if="adminClubs">
        <v-list-item
            v-for="club in adminClubs"
            :key="club.id"
            density="compact"
            :to="clubTo(club.id)"
        >
          <v-list-item-title class="font-weight-bold d-flex align-center" style="font-size:14px;">
            <v-icon class="mr-2" size="x-small" icon="mdi-shield-account-outline"/>
            {{ club.name }}
          </v-list-item-title>
        </v-list-item>
      </template>
      <v-list-item v-else density="compact">
        <v-list-item-title class="d-flex align-center" style="font-size:14px;">
          <v-progress-circular indeterminate size="14" width="2" class="mr-2"/>
          Loading your clubs…
        </v-list-item-title>
      </v-list-item>
      <v-divider/>
      <v-list-item density="compact" :to="{name: 'ClubCreate'}">
        <v-list-item-title class="font-weight-bold d-flex align-center text-primary" style="font-size:14px;">
          <v-icon class="mr-2" size="x-small" icon="mdi-plus"/>
          Create a new club
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<style scoped>
.mcta-list {
  min-width: 230px;
}

.mcta-label {
  padding: 8px 16px 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
}
</style>
